import {
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    sendPasswordResetEmail,
    updateProfile
} from 'firebase/auth';
import { auth, db } from '../../firebase/firebaseConfig';
import { doc, getDoc, setDoc, query, collectionGroup, where, getDocs } from 'firebase/firestore';
import { COLLECTIONS } from '../../constants/collections';

/**
 * Sign in user with email and password
 */
export const signIn = async (email, password) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const userData = await getUserData(userCredential.user.uid);

    return {
        user: userCredential.user,
        userData
    };
};

/**
 * Sign out current user
 */
export const logout = async () => {
    await signOut(auth);
};

export const signUpTenant = async (email, password, tenantData, userData) => {
    // 1. Create Auth User
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const userId = userCredential.user.uid;

    // 2. Create Tenant Document
    const tenantRef = doc(db, COLLECTIONS.TENANTS, `tenant_${userId}`);
    await setDoc(tenantRef, {
        ...tenantData,
        adminId: userId,
        createdAt: new Date(),
        isActive: true
    });

    const fullUserData = {
        id: userId,
        ...userData,
        email,
        tenantId: tenantRef.id,
        tenantName: tenantData.name,
        role: 'coordinator',
        createdAt: new Date(),
        lastLoginAt: new Date(),
        isActive: true
    };

    // 3. Create Tenant User Document (Full Profile) - ONLY here.
    // Explicitly using 'users' subcollection string
    const userPath = `${COLLECTIONS.TENANTS}/${tenantRef.id}/users/${userId}`;
    console.log('[Register] Creating Tenant User Profile at:', userPath);
    await setDoc(doc(db, COLLECTIONS.TENANTS, tenantRef.id, 'users', userId), fullUserData);

    // 4. Update Auth Profile
    if (userData.fullName) {
        await updateProfile(userCredential.user, {
            displayName: userData.fullName
        });
    }

    return { user: userCredential.user, tenantId: tenantRef.id };
};

export const createUser = async (email, password, userData) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const userId = userCredential.user.uid;

    if (userData.tenantId) {
        // Tenant User - Write ONLY to tenant subcollection
        const userPath = `${COLLECTIONS.TENANTS}/${userData.tenantId}/users/${userId}`;
        console.log('[CreateUser] Creating Tenant User Profile at:', userPath);

        await setDoc(doc(db, COLLECTIONS.TENANTS, userData.tenantId, 'users', userId), {
            id: userId,
            ...userData,
            email,
            createdAt: new Date(),
            lastLoginAt: new Date(),
            isActive: true
        });
    } else {
        console.warn('Attempted to create user without tenantId. In pure tenant architecture, this might be orphaned.');
        // If we inevitably strictly need a place for non-tenant admins, 
        // they might still need a root place or a "system" tenant. 
        // For now, we assume all users belong to a tenant.
    }

    if (userData.fullName) {
        await updateProfile(userCredential.user, {
            displayName: userData.fullName
        });
    }

    return userCredential.user;
};

export const getUserData = async (userId) => {
    try {
        // PURE TENANT ARCHITECTURE SEARCH
        // Scan all 'users' subcollections to find this user
        // Note: Requires "users" Collection Group Index in Firestore console if strictly enforced,
        // but for small datasets or with ID queries it often works or prompts for index creation.

        // We look for documents in ANY 'users' collection where documentId == userId
        // However, 'where(documentId())' is tricky in CollectionGroup.
        // It's safer to query the 'id' field which we explicitly save.

        const usersQuery = query(
            collectionGroup(db, 'users'),
            where('id', '==', userId)
        );

        const querySnapshot = await getDocs(usersQuery);

        if (!querySnapshot.empty) {
            // Found the user!
            const userDoc = querySnapshot.docs[0];
            const data = userDoc.data();

            // Should verify it belongs to a tenant
            if (data.tenantId) {
                // ENRICHMENT: If this is a transporter user, fetch the transporter name
                if (data.scope?.transporterId) {
                    try {
                        const tPath = `${COLLECTIONS.TENANTS}/${data.tenantId}/${COLLECTIONS.TRANSPORTERS}/${data.scope.transporterId}`;
                        const tSnap = await getDoc(doc(db, tPath));
                        if (tSnap.exists()) {
                            data.transporterName = tSnap.data().companyName;
                            data.transporterShortName = tSnap.data().shortName;
                            data.transporterId = data.scope.transporterId;
                            data.supplierCode = tSnap.data().supplierCode;
                        }
                    } catch (e) {
                        console.error('Error enriching transporter data:', e);
                    }
                }
                return { ...data, id: userId, uid: userId };
            }
        }

        console.warn('User not found in any tenant.');
        return null;

    } catch (error) {
        console.error('Error fetching user data via Collection Group:', error);
        return null;
    }
};

/**
 * Reset password
 */
export const resetPassword = async (email) => {
    await sendPasswordResetEmail(auth, email);
};

/**
 * Get current user
 */
export const getCurrentUser = () => {
    return auth.currentUser;
};

/**
 * Check if user has specific role
 */
export const hasRole = (userData, role) => {
    return userData?.role === role;
};

/**
 * Check if user has any of the specified roles
 */
export const hasAnyRole = (userData, roles) => {
    return roles.includes(userData?.role);
};

export default {
    signIn,
    logout,
    createUser,
    signUpTenant,
    getUserData,
    resetPassword,
    getCurrentUser,
    hasRole,
    hasAnyRole
};
